// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';

// MUI components
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';

// Section for import custom components --------------------------------------------------------------------------------
import CommonUtility from '@utils/Common';
import { AppConfigContext } from '@providers/AppConfigProvider';
import { useMeta } from '@providers/MansionNavigation/MetaProvider';
import { SelectedCitiesContext, useUpdateSelectedCities } from '@providers/MansionNavigation/SelectedCitiesProvider';

// Section for type definition -----------------------------------------------------------------------------------------
import filterEntries from '@components/MansionNavigation/Utils/Filters';
import generateSortQuery from '@components/MansionNavigation/Utils/Sort';
import CustomCheckboxEvent from '@/types/event/CustomCheckboxEvent';
import { CityApiData } from '@/types/components/MansionNavigation/CitiesApiData';
import CitiesListData, { CityListData } from '@/types/components/MansionNavigation/CitiesListData';

type CityGroupData = CityApiData[];

const commonUtil = CommonUtility;
const citiesJsonUrl: string = '/karte/api/v1/cities/';

// TODO: city group id が 1-23ku と 1-23ku 以外の2種類あるので、1-23ku は 0 に変換する。テーブルが正規化されたら削除
function convertGroupId(value: string | null): number {
  if (value === '1-23ku') {
    return 0;
  }
  if (value == null) {
    return 0;
  }
  return 1;
}

export default function CitiesList(props: any): React.JSX.Element {
  const { selectedPrefecture } = props;
  const { isMobile } = useContext(AppConfigContext);
  const { meta } = useMeta();

  const filterQuery = filterEntries(meta.filters)
    ? `?${filterEntries(meta.filters)}${generateSortQuery(meta.sort, '&')}`
    : `${generateSortQuery(meta.sort, '&')}`;

  const apiQueryParameter: string = `?prefecture_code=${selectedPrefecture.pref_code_name}`;

  const selectedCities = useContext(SelectedCitiesContext);
  const setSelectedCities = useUpdateSelectedCities();

  const stateCityGroupData: CityGroupData = [];
  const [cityGroupData, setCityGroupData] = useState(stateCityGroupData);

  const stateCitiesData: CitiesListData = [];
  const [citiesData, setCitiesData] = useState(stateCitiesData);

  function getPreSelectedCityIds(): number[] {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const preSelectedCityIds: string | null = urlParams.get('pre_selected_city_id');
    const returnValue: string[] = preSelectedCityIds ? preSelectedCityIds.split(',') : [];
    return commonUtil.convertTypeArrayNumber(returnValue);
  }

  function grantSelected(checkCitiesData: CitiesListData, checkSelectedCityIds: number[]): void {
    if (checkSelectedCityIds.length === 0) {
      checkCitiesData.map((cityData: CityListData) => {
        // eslint-disable-next-line no-param-reassign
        cityData.selected = false;
        return cityData;
      });
      return;
    }

    checkCitiesData.map((cityData: CityListData) => {
      if (checkSelectedCityIds.find((checkSelectedCityId) => checkSelectedCityId === Number(cityData.id_number))) {
        // eslint-disable-next-line no-param-reassign
        cityData.selected = true;
      } else {
        // eslint-disable-next-line no-param-reassign
        cityData.selected = false;
      }
      return cityData;
    });
  }

  function grantPreSelected(citiesDataId: number, checkPreSelectedCityIds: number[]): boolean {
    let returnValue: boolean = false;
    const result = checkPreSelectedCityIds.find((preSelectedCityId: number) => preSelectedCityId === citiesDataId);

    if (result !== undefined) {
      returnValue = true;
    }
    return returnValue;
  }

  const getCities = (url: string) => {
    return new Promise((resolve) => {
      fetch(url)
        .then((res) => res.json())
        .then((data) => resolve(data));
    });
  };

  const loadCities = async (data: any, checkPreSelectedCityIds: number[]): Promise<void> => {
    const citiesRecord: any = await Promise.all(
      data.results.map((city: any) => {
        const citiesRecordMap: CityListData = {
          key: `cities-${commonUtil.convertNumberToString(city.id)}`,
          id: commonUtil.convertNumberToString(city.id),
          id_number: city.id,
          city_group_id: convertGroupId(city.city_group_code_name),
          // TODO:テーブルが正規化されたら削除
          // city_group_id: city.city_group_id,
          city_group_id_sort: city.city_group_id_sort,
          city_group_name: city.city_group_name,
          city_group_code_name: city.city_group_code_name,
          city_groups_primary_sort_id: city.city_groups_primary_sort_id,
          city_groups_secondary_sort_id: city.city_groups_secondary_sort_id,
          jis_code_city_id: city.jis_code_city_id,
          mrweb_city_id: city.mrweb_city_id,
          prefecture_id: city.prefecture_id,
          city_code_name: city.city_code_name,
          city_type: city.city_type,
          city_name: city.city_name,
          district_name: city.district_name,
          city_kana: city.city_kana,
          primary_sort_id: city.primary_sort_id,
          secondary_sort_id: city.secondary_sort_id,
          mansion_count: city.mansion_count,
          estate_mansion_count: city.estate_mansion_count,
          selected: grantPreSelected(city.id, checkPreSelectedCityIds),
          created_at: city.created_at,
          updated_at: city.updated_at,
        };
        // console.log(citiesRecordMap);
        return citiesRecordMap;
      }),
    );

    const initCityGroupData: any = Object.values(commonUtil.groupBy(citiesRecord, 'city_group_code_name'));
    setCityGroupData(initCityGroupData);
    setCitiesData(citiesRecord);
  };

  useEffect(() => {
    const preSelectedCityIds = getPreSelectedCityIds();
    setSelectedCities(preSelectedCityIds);

    const fetchCitiesData = async (): Promise<void> => {
      const data: any = await getCities(citiesJsonUrl + apiQueryParameter);
      loadCities(data, preSelectedCityIds).then();
    };
    fetchCitiesData().then();
  }, []);

  const handleChange = (event: CustomCheckboxEvent): void => {
    let formSelectedCitiesArray: number[];
    formSelectedCitiesArray = selectedCities.concat();

    if (event.target.checked) {
      formSelectedCitiesArray.push(Number(event.target.id));
      setSelectedCities(formSelectedCitiesArray);
      grantSelected(citiesData, formSelectedCitiesArray);
    } else {
      formSelectedCitiesArray = selectedCities.concat();
      const mapFormSelectedCities: any = commonUtil.convertArrayToObject(formSelectedCitiesArray);
      delete mapFormSelectedCities[event.target.id];

      formSelectedCitiesArray = Object.values(mapFormSelectedCities);
      setSelectedCities(formSelectedCitiesArray);
      grantSelected(citiesData, formSelectedCitiesArray);
    }
  };

  // Section for render component --------------------------------------------------------------------------------------
  return (
    <Box className="md:mx-24">
      {isMobile ? (
        // Section for render Mobile mode ------------------------------------------------------------------------------
        <Container className="p-0">
          <List key="citiesList" className="p-0">
            {cityGroupData.map((cityGroup: any, index: number) => (
              <ListItem key={`prefecture-group-${index.toString()}`} className="block p-0">
                <List
                  key={cityGroup.key}
                  className={`cities_list p-0 ${
                    index === cityGroupData.length - 1 ? 'mb-0' : 'mb-8'
                  } border-b-[#D9D9D9] border-b border-solid`}
                >
                  <Box className="pb-4 border-b-[#D5D5D5] border-b border-solid">
                    <Typography
                      variant="h2"
                      gutterBottom
                      className="text-lg font-bold tracking-normal mb-0 mx-5 pl-[18px] border-l-2 border-l-[#191919] border-solid"
                    >
                      {selectedPrefecture.pref_name}
                      {cityGroupData.length > 1 && ' - '}
                      {cityGroup[index].city_group_name}
                    </Typography>
                  </Box>
                  {citiesData
                    .filter((cityData: any) => cityData.city_group_id === cityGroup[index].city_group_id)
                    .map((city: any) => (
                      <ListItem
                        key={`prefecture-${cityGroup[index].city_group_id}-${city.id}`}
                        className="cities relative w-[calc(100%_-_20px)] ml-5 border-b-[#D9D9D9] border-b border-solid p-0"
                      >
                        {city.mansion_count > 0 ? (
                          <>
                            <Checkbox
                              className="cities_check mr-3 p-0"
                              onChange={handleChange}
                              id={city.id}
                              checked={city.selected}
                            />
                            <CheckIcon className="cities_check_icon hidden absolute w-5 h-5 text-white" />
                            <Link
                              href={`/karte/${selectedPrefecture.pref_code_name}/cities/${city.city_code_name}/${filterQuery}`}
                              className="block w-full py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] no-underline"
                            >
                              {city.city_name}
                            </Link>
                          </>
                        ) : (
                          <>
                            <Checkbox className="cities_check mr-3 p-0" id={city.id} disabled />
                            <Typography className="opacity-60 py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left">
                              {city.city_name}
                            </Typography>
                          </>
                        )}
                      </ListItem>
                    ))}
                </List>
              </ListItem>
            ))}
          </List>
        </Container>
      ) : (
        // Section for render PC mode ----------------------------------------------------------------------------------
        <Box>
          <List key="citiesList" className="p-0">
            {cityGroupData.map((cityGroup: any, index: number) => (
              <ListItem key={`prefecture-group-${index.toString()}`} className="block w-full p-0 mt-6">
                <List key={cityGroup.key} className="flex flex-wrap justify-start p-0">
                  <Box className="w-full mb-6 pb-3 border-b-[#D5D5D5] border-b border-solid">
                    <Typography
                      variant="h2"
                      gutterBottom
                      className="text-lg font-bold leading-6 tracking-[0.08em] text-left pl-4 border-l-2 border-[#191919] mb-0"
                    >
                      {selectedPrefecture.pref_name}
                      {cityGroupData.length > 1 && ' - '}
                      {cityGroup[index].city_group_name}
                    </Typography>
                  </Box>
                  {citiesData
                    .filter((cityData: any) => cityData.city_group_id === index)
                    .map((city: any) => (
                      <ListItem
                        key={`prefecture-${cityGroup[index].city_group_id}-${city.id}`}
                        className="cities relative flex p-0 mb-6 w-1/4 self-center"
                      >
                        {city.mansion_count > 0 ? (
                          <>
                            <Checkbox
                              className="cities_check"
                              onChange={handleChange}
                              id={city.id}
                              checked={city.selected}
                            />
                            <CheckIcon className="cities_check_icon hidden absolute w-5 h-5 text-white" />
                            <Link
                              href={`/karte/${selectedPrefecture.pref_code_name}/cities/${city.city_code_name}/${filterQuery}`}
                              className="text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] rf-hover-text"
                            >
                              {city.city_name}
                            </Link>
                          </>
                        ) : (
                          <>
                            <Checkbox className="cities_check" id={city.id} disabled />
                            <Typography className="opacity-60 text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919]">
                              {city.city_name}{' '}
                            </Typography>
                          </>
                        )}
                      </ListItem>
                    ))}
                </List>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
